import { useState, type ReactNode } from 'react';

import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  type Account,
  type CareCircle,
  type CareCircleMember,
} from '@karehero/models';
import { getInitials, formatFullName } from '@karehero/helpers/name';
import { Dialog } from '@/components/atoms/Dialog/Dialog';
import SideDrawer from '@/components/molecules/SideDrawer/SideDrawer';
import DataTable, {
  type GridColDef,
} from '../../organisms/DataTable/DataTable';
import AdminTablePage from '../../templates/AdminTablePage/AdminTablePage';
import { formatRoleName } from '../../../utils/format';

export interface ResolvedCareCircleMember extends CareCircleMember {
  careCircle?: CareCircle;
  isLoading?: boolean;
}

export interface AdminAccountsPageProps {
  columns: GridColDef[];
  rows: any[];
  actions: any[];
  toolbarActions?: ReactNode;
  drawerAccount: Account | null;
  drawerCareCircles: ResolvedCareCircleMember[];
  onDrawerClose: () => void;
  onCareCircleClick: (careCircleID: string) => void;
  deleteEmail?: string;
  isDeleteOpen: boolean;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  paginationMode?: 'client' | 'server';
  rowCount?: number;
  page?: number;
  onPaginationChange?: (page: number, pageSize: number) => void;
  rowsPerPage?: number;
  isLoading?: boolean;
  isError?: boolean;
  onSearchChange?: (search: string) => void;
  searchValue?: string;
}

const CareCircleListItem = ({
  member,
  onClick,
}: {
  member: ResolvedCareCircleMember;
  onClick: () => void;
}) => {
  const recipient = member.careCircle?.careRecipientAccount;
  const recipientName = recipient
    ? formatFullName(recipient.firstName, recipient.lastName)
    : undefined;
  const roles = member.roles?.map((r) => r.name).join(', ');

  return (
    <ListItemButton disabled={member.isLoading} onClick={onClick}>
      <ListItemAvatar>
        {member.isLoading ? (
          <Skeleton variant='circular' width={36} height={36} />
        ) : (
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'primary.light',
              color: 'primary.main',
              fontSize: 14,
            }}
          >
            {recipientName ? getInitials(recipientName) : '?'}
          </Avatar>
        )}
      </ListItemAvatar>
      <ListItemText
        primary={
          member.isLoading ? (
            <Skeleton width={120} />
          ) : (
            (recipientName ?? member.careCircleID)
          )
        }
        secondary={member.isLoading ? <Skeleton width={80} /> : roles}
      />
    </ListItemButton>
  );
};

const AdminAccountsPage = ({
  columns,
  rows,
  actions,
  toolbarActions,
  drawerAccount,
  drawerCareCircles,
  onDrawerClose,
  onCareCircleClick,
  deleteEmail,
  isDeleteOpen,
  onDeleteClose,
  onDeleteConfirm,
  paginationMode,
  rowCount,
  page,
  onPaginationChange,
  rowsPerPage,
  isLoading,
  isError,
  onSearchChange,
  searchValue,
}: AdminAccountsPageProps) => {
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  return (
    <Box
      sx={{
        minHeight: '100%',
      }}
    >
      <AdminTablePage title='Accounts'>
        <DataTable
          columns={columns}
          rows={rows}
          actions={actions}
          toolbarActions={toolbarActions}
          paginationMode={paginationMode}
          rowCount={rowCount}
          page={page}
          onPaginationChange={onPaginationChange}
          rowsPerPage={rowsPerPage}
          isLoading={isLoading}
          isError={isError}
          onSearchChange={onSearchChange}
          searchValue={searchValue}
        />
      </AdminTablePage>

      <SideDrawer
        open={Boolean(drawerAccount)}
        onClose={onDrawerClose}
        header={
          <>
            <Typography
              variant='h3'
              sx={{
                color: 'primary.main',
              }}
            >
              {drawerAccount?.fullName}
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: 'text.secondary',
                mt: 0.5,
              }}
            >
              {drawerAccount?.email}
            </Typography>
          </>
        }
      >
        {drawerAccount && (
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                }}
              >
                Organisation
              </Typography>
              <Typography variant='body1'>
                {drawerAccount.organization?.name ?? '—'}
              </Typography>
            </Stack>

            <Stack spacing={1}>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                }}
              >
                Account Role
              </Typography>
              <Chip
                label={formatRoleName(drawerAccount.accountRole?.name)}
                size='small'
                sx={{ width: 'fit-content' }}
              />
            </Stack>

            {drawerAccount.kindeEmail && (
              <Stack spacing={1}>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Kinde Email
                </Typography>
                <Typography variant='body1'>
                  {drawerAccount.kindeEmail}
                </Typography>
              </Stack>
            )}

            <Divider />

            <Typography
              variant='h4'
              sx={{
                color: 'primary.main',
              }}
            >
              Care circles
            </Typography>

            {drawerCareCircles.length > 0 ? (
              <List disablePadding>
                {drawerCareCircles.map((member) => (
                  <CareCircleListItem
                    key={member.id}
                    member={member}
                    onClick={() => onCareCircleClick(member.careCircleID)}
                  />
                ))}
              </List>
            ) : (
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                }}
              >
                Not a member of any care circles.
              </Typography>
            )}
          </Stack>
        )}
      </SideDrawer>

      <Dialog
        variant='dialog'
        isOpen={isDeleteOpen}
        setIsOpen={(open) => {
          if (!open) {
            setDeleteConfirmation('');
            onDeleteClose();
          }
        }}
        title='Are you sure you want to delete this account?'
        description={deleteEmail}
        zIndex={30}
      >
        <Stack spacing={2}>
          <TextField
            label='Type the email address to confirm'
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            size='small'
            fullWidth
          />
          <Button
            variant='contained'
            disabled={deleteConfirmation !== deleteEmail}
            onClick={() => {
              onDeleteConfirm();
              setDeleteConfirmation('');
            }}
          >
            Delete Account
          </Button>
        </Stack>
      </Dialog>
    </Box>
  );
};

AdminAccountsPage.displayName = 'AdminAccountsPage';
export default AdminAccountsPage;
